import { useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ActivityIndicator, IconButton, useTheme } from 'react-native-paper'

import { Image } from 'react-native'
import { AuthContext } from '../../../../contexts/auth-context'
import { WechatPNG } from '../../../../utils/meldd-icons'

import Toast from 'react-native-root-toast'
import { Env } from '../../../../env'
import { MessageType } from '../../../../utils/message-type'
import { Wechat, WechatWrapper } from '../../utils/wechat.login'
import { isWeChatBrowser, isDesktopBrowser } from '../../../../utils/browser'
import {
  useLinkWithWechatMutation,
  User,
  useWechatCheckMutation,
  useWechatLoginMutation,
} from '../../../../generated/graphql'
import { RegisterSSOModal, RegisterSSOModalRef } from './register-sso.modal'
import { WechatQRModal } from './wechat-qr.modal'
import * as Sentry from '@sentry/react-native'

export interface WechatLogin {
  callbackOnly?: boolean
  disabled?: boolean
  autoStart?: boolean
  redirectPath?: string
  onMergeAccount?: (tokenId: string) => void
  customButton?: (props: {
    disabled?: boolean
    onPress: () => void
  }) => JSX.Element
  onSuccess?: (user?: User) => void
  onAccountExists?: () => void
  onLinkingError?: () => void
}

export default function WechatLogin({
  disabled,
  autoStart,
  onMergeAccount,
  customButton,
  onSuccess,
  redirectPath,
  callbackOnly,
  onAccountExists,
  onLinkingError,
}: WechatLogin) {
  const { t } = useTranslation('login_register_reset')
  const wechat = useRef<WechatWrapper>()
  const modalRef = useRef<RegisterSSOModalRef>(null)
  const { setCurrentUser } = useContext(AuthContext)
  const user = useContext(AuthContext)

  const [loading, setIsLoading] = useState(false)
  const [showQrCode, setShowQrCode] = useState(false)
  const [wechatCode, setWechatCode] = useState('')
  const [wechatReady, setWechatReady] = useState(false)
  const hasAutoStarted = useRef(false)

  const theme = useTheme()
  const [doCheckAccount] = useWechatCheckMutation()
  const [doWechatLogin] = useWechatLoginMutation()
  const [doLinkWithWechat] = useLinkWithWechatMutation()

  /**
   * LOAD
   */
  useEffect(() => {
    async function setup() {
      wechat.current = await Wechat(Env.WECHAT_WEB_ID)
      await wechat.current.setup()
      setWechatReady(true)
    }
    setup()
  }, [])

  useEffect(() => {
    if (!autoStart || disabled || !wechatReady || hasAutoStarted.current) {
      return
    }
    hasAutoStarted.current = true
    wechatLogin()
  }, [autoStart, disabled, wechatReady])

  /**
   * REDIRECT
   */
  useEffect(() => {
    async function wechatCallback() {
      const wechat = await Wechat(Env.WECHAT_WEB_ID)
      const code = (await wechat.callback()) || ''
      if (!code) {
        return
      }
      setWechatCode(code)
      // Using isAnonymous instead of userId since userId is always initialized with uuidv4() for anonymous users
      !user.isAnonymous ? check(code) : login(code)
    }
    wechatCallback()
  }, [])

  /**
   * REQUEST
   */
  const wechatLogin = async () => {
    if (!wechat.current) {
      return
    }

    setIsLoading(true)
    if (!isWeChatBrowser()) {
      setShowQrCode(true)
    }

    const wechatInstalled =
      (await wechat.current?.isInstalled()) as unknown as {
        id: string
        success: boolean
      }

    if (!wechatInstalled) {
      setIsLoading(false)
      setShowQrCode(false)
      return Toast.show(t('Wechat not installed'), MessageType.error)
    }

    const result = await wechat.current.auth(redirectPath || '')
    if (!result) {
      setShowQrCode(false)
    }
    setIsLoading(false)
  }

  /**
   * Functions to connect with our backend
   */
  const login = async (code: string) => {
    try {
      const result = await doWechatLogin({
        variables: {
          input: { code, provider: isDesktopBrowser() ? 'open' : 'pac' },
        },
      })
      setCurrentUser(result.data!.wechatLogin)
      Toast.show(t('Login successful'), MessageType.info)
      onSuccess?.()
    } catch (error: unknown) {
      Toast.show((error as Error).message.toString(), MessageType.error)
      console.error(error)
      Sentry.captureException(error)
    }
  }

  const check = async (code: string) => {
    try {
      const result = await doCheckAccount({
        variables: {
          input: { code, provider: isDesktopBrowser() ? 'open' : 'pac' },
        },
      })
      // Only link WeChat if no existing account is found
      !result.data?.wechatCheck.accountExists ? link(code) : onAccountExists?.()
    } catch (error: unknown) {
      Toast.show((error as Error).message.toString(), MessageType.error)
      console.error(error)
      Sentry.captureException(error)
    }
  }

  const link = async (code: string) => {
    try {
      await doLinkWithWechat({
        variables: {
          input: { code, provider: isDesktopBrowser() ? 'open' : 'pac' },
        },
      })
      Toast.show(t('wechatLinkSuccessful'), MessageType.info)
      onSuccess?.(user)
    } catch (error: unknown) {
      Toast.show((error as Error).message.toString(), MessageType.error)
      onLinkingError?.()
      console.error(error)
      Sentry.captureException(error)
    }
  }

  // The component only do callback logic, we don't render anything
  if (callbackOnly) {
    return null
  }

  // We hide on global, but allow on staging and local
  if (Env.IS_PRODUCTION && !Env.IS_CN) {
    return null
  }

  // Scanning QR code on mobile doesn't work well
  // if (Env.IS_RESPONSIVE_WEB) {
  //   return null
  // }
  return (
    <>
      {customButton ? (
        customButton({ disabled, onPress: () => wechatLogin() })
      ) : (
        <IconButton
          mode="outlined"
          icon={() =>
            loading ? (
              <ActivityIndicator size="small" color={theme.colors.primary} />
            ) : (
              <Image source={WechatPNG} style={[{ width: 16, height: 16 }]} />
            )
          }
          disabled={disabled}
          onPress={() => wechatLogin()}
        />
      )}
      <WechatQRModal
        visible={showQrCode}
        loading={loading}
        onDismiss={() => setShowQrCode(false)}
      />
      <RegisterSSOModal
        ref={modalRef}
        ssoName={t('wechat')}
        onNewAccount={() => {
          login(wechatCode)
          modalRef.current?.close()
        }}
        mergeAccount={() => {
          onMergeAccount?.(wechatCode)
          modalRef.current?.close()
        }}
      />
    </>
  )
}
